import { apiSimulator } from './api-simulator.service';
import {
  CoachMarket,
  CoachContract,
  CoachOffer,
  CoachOfferDto,
} from '../models/coaches.types';

/**
 * Cliente HTTP para el servicio de Entrenadores (Coaches)
 * Rutas base: /api/clubs/{club}/coaches/leagues/{group}
 */
export const coachesApi = {
  /**
   * GET /api/clubs/{club}/coaches/leagues/{group}/market
   * Obtiene el mercado diario de entrenadores para el club y grupo de competición.
   */
  async getMarket(clubId: string, groupId: string): Promise<CoachMarket> {
    const endpoint = `/api/clubs/${encodeURIComponent(clubId)}/coaches/market`;
    const res = await apiSimulator.callWithMockFallback<CoachMarket>({
      method: 'GET',
      endpoint,
      service: 'coaches',
      mockName: 'market',
      allowMockFallback: false,
    });
    // El servidor puede devolver únicamente {"listings":[]}
    if (!res || !Array.isArray(res.listings)) {
      return {
        listings: [],
        ...(res || {}),
      };
    }
    return res;
  },

  /**
   * GET /api/clubs/{club}/coaches/leagues/{group}/contracts
   * Obtiene el historial de contratos del club, ordenado por signed_at descendente.
   */
  async getContracts(clubId: string, groupId: string): Promise<CoachContract[]> {
    const endpoint = `/api/clubs/${encodeURIComponent(clubId)}/coaches/contracts`;
    const res = await apiSimulator.callWithMockFallback<CoachContract[]>({
      method: 'GET',
      endpoint,
      service: 'coaches',
      mockName: 'contracts',
      allowMockFallback: false,
    });
    return Array.isArray(res) ? res : [];
  },

  /**
   * GET /api/clubs/{club}/coaches/leagues/{group}/offers
   * Incluye las ofertas pendientes y el historial reciente del club (hasta 100).
   */
  async getOffers(clubId: string, groupId: string): Promise<CoachOffer[]> {
    const endpoint = `/api/clubs/${encodeURIComponent(clubId)}/coaches/offers`;
    const res = await apiSimulator.callWithMockFallback<CoachOffer[]>({
      method: 'GET',
      endpoint,
      service: 'coaches',
      mockName: 'offers',
      allowMockFallback: false,
    });
    if (!Array.isArray(res)) throw new Error('No se pudieron verificar las ofertas de entrenadores.');
    return res;
  },

  /**
   * PUT /api/clubs/{club}/coaches/leagues/{group}/markets/{market}/coaches/{coach}/offers/{id}
   * Registra una oferta idempotente para un entrenador en el mercado activo.
   * El cuerpo debe enviarse en camelCase: { signingBonus, annualSalary, releaseClause }
   */
  async submitOffer(
    clubId: string,
    groupId: string,
    marketId: string,
    coachId: string,
    offerId: string,
    body: CoachOfferDto
  ): Promise<void> {
    const endpoint = `/api/clubs/${encodeURIComponent(clubId)}/coaches/markets/${encodeURIComponent(marketId)}/coaches/${encodeURIComponent(
      coachId
    )}/offers/${encodeURIComponent(offerId)}`;

    await apiSimulator.callWithMockFallback<void>({
      method: 'PUT',
      endpoint,
      body,
      service: 'coaches',
      mockName: 'submit-offer',
      allowMockFallback: false,
    });
  },

  /**
   * DELETE /api/clubs/{club}/coaches/leagues/{group}/offers/{id}
   * Cancela una oferta pendiente antes del cierre del mercado.
   */
  async cancelOffer(clubId: string, groupId: string, offerId: string): Promise<void> {
    const endpoint = `/api/clubs/${encodeURIComponent(clubId)}/coaches/offers/${encodeURIComponent(offerId)}`;

    await apiSimulator.callWithMockFallback<void>({
      method: 'DELETE',
      endpoint,
      service: 'coaches',
      mockName: 'cancel-offer',
      allowMockFallback: false,
    });
  },
};
